import React from 'react';
import { withStyles, WithStyles } from '@material-ui/core';
import ArrowDropDown from '@material-ui/icons/KeyboardArrowDown';
import Select, { components } from 'react-select';
import type { OptionProps } from 'react-select';
import StyledTooltip from '@StyledToolTip';

type GridSelectFilterTheme = {
  textFontSize: string | number;
  iconColor: string;
  TextColor: string;
  ModalBGColor: string;
  HighlightColor: string;
  HeaderSelectedText: string;
};

const styles = (theme: GridSelectFilterTheme) => ({
  selectFilter: {
    '& .select__control': {
      minWidth: 160,
      backgroundColor: 'transparent',
      minHeight: 28,
      maxHeight: 28,
      boxSizing: 'border-box' as const,
      borderRadius: 2,
      fontSize: theme.textFontSize,
      border: `1px solid ${theme.iconColor}`,
      cursor: 'pointer',
      '&:hover,&.select__control--is-focused': {
        boxShadow: 'none',
        border: `1px solid ${theme.iconColor}`,
      },
      '& .select__value-container': {
        height: 24,
        color: theme.TextColor,
      },
      '& .select__single-value': {
        color: 'inherit',
      },
      '& .select__placeholder': {
        top: '50%',
      },
      '& .select__input': {
        color: theme.TextColor,
      },
      '& .select__indicators': {
        height: 24,
        '& .select__indicator': {
          padding: 5,
          height: 24,
        },
        '& .select__indicator-separator': {
          height: 24,
          margin: 0,
          display: 'none',
        },
      },
    },
    '& .select__menu': {
      margin: 0,
      maxHeight: 140,
      border: `1px solid ${theme.iconColor}`,
      borderRadius: '0 0 2px 2px',
      borderTop: 'none',
      '& .select__menu-list': {
        maxHeight: 139,
        borderRadius: 2,
        fontSize: theme.textFontSize,
        backgroundColor: theme.ModalBGColor,
        color: theme.TextColor,
      },
      '& .select__option--is-focused': {
        backgroundColor: theme.HighlightColor,
        cursor: 'pointer',
      },
      '& .select__option--is-selected': {
        backgroundColor: theme.HeaderSelectedText,
      },
    },
  },
  filterDropdownIconStyle: {
    cursor: 'pointer',
    color: theme.iconColor,
    marginRight: 4,
    marginTop: 3,
    fontSize: 18,
  },
});

export interface GridSelectFilterOption {
  label: string;
  value: string;
  title?: string;
}

export type GridSelectFilterProps<T extends GridSelectFilterOption = GridSelectFilterOption> = {
  value: T | null;
  onChange: (option: T) => void;
  options: readonly T[];
} & WithStyles<typeof styles>;

/**
 * Shared header select filter used by Pickups and Trailer Moves grids.
 * Pass grid-specific options / value / onChange from thin wrappers.
 */
const GridSelectFilter = <T extends GridSelectFilterOption>({
  classes,
  value,
  onChange,
  options,
}: GridSelectFilterProps<T>) => (
  <Select<T>
    value={value}
    onChange={(option) => {
      if (option) {
        onChange(option);
      }
    }}
    options={[...options]}
    classNamePrefix="select"
    className={classes.selectFilter}
    menuPlacement="auto"
    menuPosition="absolute"
    closeMenuOnSelect
    components={{
      DropdownIndicator: (props) => (
        <ArrowDropDown {...props.innerProps} className={classes.filterDropdownIconStyle} />
      ),
      Option: (props: OptionProps<T>) => (
        <components.Option {...props}>
          <StyledTooltip title={props.data.title ?? ''}>
            <div>{props.data.label}</div>
          </StyledTooltip>
        </components.Option>
      ),
    }}
  />
);

export default withStyles(styles)(GridSelectFilter) as <T extends GridSelectFilterOption>(
  props: Omit<GridSelectFilterProps<T>, 'classes'>
) => React.ReactElement;
